import { Grid } from '../common/Grid';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, Button, TextField, Select, MenuItem, FormControl, InputLabel,
  Checkbox, Chip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, InputAdornment, Divider, Stack,
  CircularProgress, Alert, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip,
  TablePagination, TableSortLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  RecordVoiceOver as CounselIcon,
  WarningAmber as WarningIcon,
  DoNotDisturb as DeclineIcon,
  PhoneInTalk as OutreachIcon,
} from '@mui/icons-material';
import { format, parseISO, isPast, startOfDay, addDays, isToday, isTomorrow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { usePharmacyStore } from '../../stores/pharmacyStore';
import { useTaskQueueStore } from '../../stores/taskQueueStore';
import { useAuth0 } from '@auth0/auth0-react';
import { usePharmacyUsers } from '../../hooks/queries/usePharmacyUsers';
import { useFilterPersistence } from '../../hooks/useFilterPersistence';
import { useTaskQueueCounts, invalidateTaskQueueCounts } from '../../hooks/queries/useTaskQueueCounts';
import { SourceThreadLink } from './SourceThreadLink';
import { AssignedToCell } from '../order-fulfillment/AssignedToCell';
import { noAssignableUsersText } from '../../constants/taskAssignment';
import { TaskDepartment } from '../../constants/taskDepartments';
import { getDepartmentAssignableUsers } from '../../utils/assignableUsers';
import { TaskQueueTabs, TaskQueueTab } from './TaskQueueTabs';
import {
  getTaskQueue, createManualTask, updateManualTask, addTaskNote,
  deleteManualTask, bulkAssignTasks, getManualTaskDetail, logOutreachAttempt,
  TaskQueueItem, TaskQueueResponse, TaskQueueFilters, TASK_TYPES, TaskTypeKey,
} from '../../services/taskQueueService';
import {
  OUTREACH_OUTCOMES, OUTREACH_SNOOZE_PRESET_DAYS, OUTREACH_ESCALATION_THRESHOLD,
  OutreachOutcomeValue,
} from '../../constants/outreachConstants';
import { getPharmacyTodayString } from '../../utils/timeUtils';
import {
  snoozeCeiling, snoozePresetDate, defaultSnoozeDueDate, validateSnoozeDueDate,
  formatSnoozeDate, snoozeFloor, hasLegalSnoozeDate,
} from './outreachSnooze';
import CounselingDialog from '../counseling/CounselingDialog';
import { DECLINED_REASONS, getCounselingStatusDisplay } from '../../constants/counselingConstants';
import api from '../../services/api';
import counselingService from '../../services/counselingService';
import type { RefillAssessmentDueItem } from '../../services/counselingService';
import CreateTaskDialog from './CreateTaskDialog';
import { TroubleshootingStepsPanel } from './TroubleshootingStepsPanel';
import { headerStripSurface, stickyTintUrgent, tintSuccessCard, tintWarning } from '../../theme/gridSurfaces';

// GP-927: shared orange accent for the refill-assessment tile + table.
const REFILL_ACCENT = '#ed6c02';

// ---- Helper functions ----

const isCounselingDeclined = (item: TaskQueueItem): boolean => {
  if (item.type !== 'counseling') return false;
  // GP-1114: prefer the linked counseling record's real status, joined by the
  // backend. This detects declines made anywhere — including on the Clinical
  // Verification screen, which never stamps the task description — where the old
  // heuristic below folded them into "Complete". Both status==='declined' and the
  // legacy completed+declinedReason shape resolve via the shared helper.
  if (item.counselingStatus) {
    return getCounselingStatusDisplay(item.counselingStatus, item.counselingDeclinedReason).isDeclined;
  }
  // Fallback heuristic when no counseling record is linked to the task.
  // Check description
  if (item.description?.toLowerCase().includes('counseling declined')) return true;
  // Check activity log notes for decline entries
  if (item.activityLog?.some(e =>
    e.note?.toLowerCase().includes('counseling declined') ||
    e.action?.toLowerCase().includes('declined')
  )) return true;
  return false;
};

/**
 * GP-1203: the task's Rx was cancelled and rebuilt, and the counseling was
 * completed against the replacement Rx. Nothing is left to do, so the row must
 * NOT age into Overdue and must NOT offer Start Counseling — it would ask staff
 * to counsel on a dead Rx (Rx 1467996 vs the rebuilt 1466777).
 * The backend decides this; the client only renders it.
 */
const isCounselingSuperseded = (item: TaskQueueItem): boolean =>
  item.type === 'counseling' &&
  !!item.counselingStatus &&
  getCounselingStatusDisplay(item.counselingStatus, item.counselingDeclinedReason).isSuperseded;

/**
 * GP-1337: a task with work still to route, i.e. one that can be assigned.
 * Completed, cancelled and superseded tasks have none — assigning them would put
 * a pharmacist's name on work that no longer exists.
 */
const isAssignable = (item: TaskQueueItem): boolean =>
  item.status !== 'completed' && item.status !== 'cancelled' && !isCounselingSuperseded(item);

const getStatusDisplay = (item: TaskQueueItem): { label: string; color: 'success' | 'error' | 'warning' | 'default' } => {
  if (item.status === 'cancelled') {
    return { label: 'Cancelled', color: 'default' };
  }
  if (item.status === 'completed') {
    if (isCounselingDeclined(item)) {
      return { label: 'Declined', color: 'default' };
    }
    return { label: 'Complete', color: 'success' };
  }
  // Superseded outranks Overdue — an orphaned task is not late, it is closed.
  if (isCounselingSuperseded(item)) return { label: 'Superseded', color: 'default' };
  if (isOverdue(item)) return { label: 'Overdue', color: 'error' };
  return { label: 'In Progress', color: 'warning' };
};

/**
 * R10 (PRX-67): a task whose source is not 'manual' — troubleshooting, a PRX-66
 * pack change, or counselling auto-created — closes with "Acknowledge" (the
 * pharmacist didn't create it; they're closing out what the system raised). A
 * manually created task keeps "Complete". Both call the same completed
 * transition (handleConfirmComplete/handleCompleteTask) — this only changes
 * the button's label.
 */
const getTaskCloseLabel = (item: TaskQueueItem): string =>
  item.source === 'manual' ? 'Complete' : 'Acknowledge';

const isOverdue = (item: TaskQueueItem): boolean => {
  if (!item.dueDate || item.status === 'completed' || item.status === 'cancelled') return false;
  if (isCounselingSuperseded(item)) return false;
  return isPast(startOfDay(parseISO(item.dueDate + 'T00:00:00')));
};

/** Tooltip/detail copy naming the Rx that closed a superseded task. */
const supersededByLabel = (item: TaskQueueItem): string =>
  item.supersededByRxNumber
    ? `Counseling was completed on the rebuilt Rx #${item.supersededByRxNumber}, which replaced this cancelled Rx.`
    : 'Counseling was completed on the prescription that replaced this cancelled Rx.';

const getTaskTypeLabel = (taskType: string | undefined): string => {
  if (!taskType) return '—';
  return TASK_TYPES[taskType as TaskTypeKey] || taskType;
};

const getLinkStatusDisplay = (linkStatus: string | undefined): { label: string; color: 'success' | 'default' } => {
  if (linkStatus === 'linked') return { label: 'Linked', color: 'success' };
  return { label: 'Unlinked', color: 'default' };
};

const formatCreatedDate = (dateStr: string): string => {
  if (!dateStr) return '—';
  // Append T00:00:00 for date-only strings to force local time parsing
  const date = parseISO(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00');
  if (isToday(date)) return 'Today';
  if (isTomorrow(date)) return 'Tomorrow';
  return format(date, 'M/d/yyyy');
};

/**
 * GP-1337: format a task date that may arrive as `YYYY-MM-DD` (a Postgres date
 * column, e.g. manual_tasks.delivery_date) or as a full ISO timestamp
 * (troubleshooting-mapped items). Appending `T00:00:00` is what keeps the former
 * on the right LOCAL day; doing it to the latter would yield an Invalid Date.
 */
const formatTaskDate = (value: string, pattern = 'M/d/yyyy'): string =>
  format(parseISO(value.includes('T') ? value : value + 'T00:00:00'), pattern);

/**
 * Activity-log actions are machine slugs (`outreach_attempted`); the pane renders
 * them verbatim, so prettify rather than leak an enum to the user (GP-1365).
 */
const formatActivityAction = (action: string): string => (action || '').replace(/_/g, ' ');

// Extract drug name from task description (format: "... Drug: NAME. ...")
const extractDrugNameFromDescription = (description?: string): string | undefined => {
  if (!description) return undefined;
  return description.match(/Drug:\s+(.+?)\./)?.[1]?.trim() || undefined;
};

// ---- Filters ----

/**
 * GP-1337: the queue's filter set, persisted per user via useFilterPersistence
 * (the same localStorage-backed mechanism the Clinical Verification, Packaging and
 * All Orders queues use) so a selection survives navigating away and back.
 * Module-level so the object identity is stable across renders.
 */
const TASK_FILTER_DEFAULTS = {
  filterStatus: 'all',
  filterDueDate: 'all',
  filterLinkedStatus: 'all',
  searchTerm: '',
  // Date range (Task Created Date) — empty string = no filter
  createdDateFrom: '',
  createdDateTo: '',
  // PRX-67 (R4): the active department/Mine tab — 'all' | a TaskDepartment | 'mine'.
  // Persisted alongside the rest of the filter set so a tab choice survives
  // leaving and returning to the queue, same as every other filter here.
  queueTab: 'all' as TaskQueueTab,
};

/**
 * One saved view for both mount points. The component is the `/tasks` page AND a
 * drawer openable from every queue, so keying on the URL path (the hook's default)
 * would give the drawer a different saved filter set on each page it opened over.
 */
const TASK_FILTER_STORAGE_KEY = 'taskQueue';

// ---- Props ----

export interface TaskQueueContentProps {
  variant: 'drawer' | 'page';
  onClose?: () => void;
  /**
   * PRX-105: lock the queue to one department, for a page that embeds it (the
   * Blister pack page's Tasks tab). Every fetch asks for that department, the
   * department tabs are not offered, and the saved filters live under their
   * own key so the /tasks page's saved view is neither read nor overwritten.
   */
  department?: TaskDepartment;
}

// ---- Component ----

export const TaskQueueContent: React.FC<TaskQueueContentProps> = ({ variant, onClose, department }) => {
  const { isOpen } = useTaskQueueStore();
  const activePharmacy = usePharmacyStore((state: any) => state.activePharmacy);
  const { user } = useAuth0();
  const { data: pharmacyUsersData } = usePharmacyUsers();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentUserEmail = user?.email || '';

  // PRX-67 (R7): open-task counts feeding the tab strip's chips. Invalidated
  // (not just left to its 30s staleTime) after any mutation that can move a
  // task across a department/mine/all bucket — see invalidateCounts below.
  // A queue locked to one department shows no tabs, so it does not fetch their
  // counts (an undefined pharmacy disables the query). It still invalidates
  // them below: the sidebar's My open tasks badge reads the same query.
  const { data: taskQueueCounts } = useTaskQueueCounts(department ? undefined : activePharmacy?.id);
  const invalidateCounts = useCallback(
    () => invalidateTaskQueueCounts(queryClient, activePharmacy?.id),
    [queryClient, activePharmacy?.id],
  );

  const isPage = variant === 'page';

  // Data state
  const [response, setResponse] = useState<TaskQueueResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // GP-927: Refill Assessment advisory list (patients whose latest completed
  // assessment is past the interval). Read-only — let-pass and call in bulk.
  const [refillDueList, setRefillDueList] = useState<RefillAssessmentDueItem[]>([]);
  const [refillLoading, setRefillLoading] = useState(false);
  const [refillTabActive, setRefillTabActive] = useState(false);

  // Filter state — matches wireframe: Status, Due Date, Linked Status.
  // GP-1337: persisted per user, so filters survive leaving and returning to the
  // queue instead of silently resetting to All.
  const [filters, updateFilter, resetFilters] = useFilterPersistence(
    TASK_FILTER_DEFAULTS,
    'filter',
    department ? `${TASK_FILTER_STORAGE_KEY}:${department}` : TASK_FILTER_STORAGE_KEY,
  );
  const {
    filterStatus, filterDueDate, filterLinkedStatus, searchTerm,
    createdDateFrom, createdDateTo,
  } = filters;
  // PRX-105: a locked queue asks for its department whatever was saved.
  const queueTab: TaskQueueTab = department ?? filters.queueTab;
  // PRX-67 (wave 1 review item 8, Low) — queueTab was omitted here even
  // though resetFilters() (below) restores it to 'all': on a non-All tab with
  // no other filter set, "Clear filters" silently vanished instead of
  // offering to reset the one filter actually active.
  const hasActiveFilters =
    filterStatus !== 'all' || filterDueDate !== 'all' || filterLinkedStatus !== 'all' ||
    !!searchTerm || !!createdDateFrom || !!createdDateTo || (!department && queueTab !== 'all');

  // Pagination + sort state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [sortBy, setSortBy] = useState<'createdAt' | 'dueDate' | 'deliveryDate'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

  // GP-1337: row selection for bulk assign, and the assign dialog it feeds.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignTargetUser, setAssignTargetUser] = useState('');
  const [assigning, setAssigning] = useState(false);

  // UI state
  const [taskDialog, setTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskQueueItem | null>(null);
  const [detailView, setDetailView] = useState<TaskQueueItem | null>(null);
  const [noteText, setNoteText] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' }>({
    open: false, message: '', severity: 'success',
  });
  const [saving, setSaving] = useState(false);
  const [counselingDialogOpen, setCounselingDialogOpen] = useState(false);
  const [counselingLoading, setCounselingLoading] = useState(false);
  const [counselingOrderData, setCounselingOrderData] = useState<Record<string, any> | null>(null);
  // GP-928: the open (pending/accepted) counseling record for the task being
  // started, so a saved-for-later session resumes with its checklist instead of blank.
  const [counselingExistingRecord, setCounselingExistingRecord] = useState<Record<string, any> | null>(null);
  // GP-1081: monotonic token identifying the latest "Start Counseling" click.
  // The order/hardcopy/record fetches in handleStartCounseling resolve
  // independently and can be slow (GET /api/orders was seen at 3+ seconds), so
  // a superseded (or out-of-order) response must never overwrite the currently
  // opened order's data — otherwise the panel shows one order's drug while the
  // checklist/record belongs to another. Each invocation captures its token and
  // discards its results if a newer click has since started.
  const counselingRequestRef = useRef(0);

  // Decline counseling state
  const [declineCounselingOpen, setDeclineCounselingOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [declineReasonOther, setDeclineReasonOther] = useState('');
  const [decliningCounseling, setDecliningCounseling] = useState(false);
  const [reopeningCounseling, setReopeningCounseling] = useState(false);

  // Confirmation dialog for completing tasks
  const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false);
  const [taskToComplete, setTaskToComplete] = useState<TaskQueueItem | null>(null);

  // GP-1365: "I tried to reach the patient — snooze this" state. The third path
  // beside completing and declining a counseling session.
  const [outreachOpen, setOutreachOpen] = useState(false);
  const [outreachOutcome, setOutreachOutcome] = useState<OutreachOutcomeValue | ''>('');
  const [outreachNote, setOutreachNote] = useState('');
  const [outreachDueDate, setOutreachDueDate] = useState('');
  const [loggingOutreach, setLoggingOutreach] = useState(false);

  // Form state moved to CreateTaskDialog component

  // ---- Data Fetching ----

  const isOpenForFetch = isPage ? true : isOpen;

  // GP-1116: monotonic token identifying the latest task-queue fetch. On mount
  // and while polling, several fetchTasks calls overlap (dependency churn from
  // activePharmacy being replaced during boot, filter/search changes, and the
  // post-create refetch). Without ordering, a slow earlier request can resolve
  // AFTER a newer one and overwrite good data with a stale/empty payload — which
  // is why the grid stayed "0-0 of 0" until a full remount even though every
  // GET /api/task-queue returned 200 with rows. Only the newest fetch may commit.
  const fetchTokenRef = useRef(0);

  const fetchTasks = useCallback(async () => {
    if (!activePharmacy?.id || !isOpenForFetch) return;
    const token = ++fetchTokenRef.current;
    setLoading(true);
    setError(null);

    try {
      // PRX-67 (D5): the counseling-only hardcode is gone — the tabs (below)
      // make the full task list navigable instead. queueTab selects either a
      // department (R3/R4) or the Mine preset (assignedTo resolved server-side
      // to the caller, R8), on top of whatever else is filtered.
      const filters: TaskQueueFilters = {
        page,
        limit,
        sortBy,
        sortOrder,
      };

      if (queueTab === 'mine') filters.assignedTo = 'me';
      else if (queueTab !== 'all') filters.department = queueTab;

      if (filterStatus !== 'all') filters.status = (filterStatus === 'overdue' || filterStatus === 'declined') ? undefined : filterStatus;
      if (filterLinkedStatus !== 'all') filters.linkStatus = filterLinkedStatus;
      if (filterDueDate !== 'all') filters.dueDateFilter = filterDueDate;
      if (searchTerm) filters.searchTerm = searchTerm;
      if (createdDateFrom) filters.createdDateFrom = createdDateFrom;
      if (createdDateTo) filters.createdDateTo = createdDateTo;

      const data = await getTaskQueue(activePharmacy.id, filters);
      // A newer fetch superseded this one — drop the result so an out-of-order
      // (or slow initial) response can never clobber fresher data.
      if (fetchTokenRef.current !== token) return;
      setResponse(data);
    } catch (err: any) {
      if (fetchTokenRef.current !== token) return;
      setError(err?.response?.data?.message || err?.message || 'Failed to load tasks');
    } finally {
      // Only the latest fetch may clear the spinner; a superseded one must leave
      // the newer request's loading state untouched.
      if (fetchTokenRef.current === token) setLoading(false);
    }
  }, [
    activePharmacy?.id, isOpenForFetch,
    filterStatus, filterLinkedStatus, filterDueDate, searchTerm,
    createdDateFrom, createdDateTo, queueTab,
    page, limit, sortBy, sortOrder,
  ]);

  useEffect(() => {
    if (isOpenForFetch) fetchTasks();
  }, [fetchTasks, isOpenForFetch]);

  // GP-927: load the refill-assessment due-list (one call) so the tile shows a
  // live count and the tab has data ready. Advisory — a failure just empties it.
  const fetchRefillDueList = useCallback(async () => {
    const pharmacyId = activePharmacy?.id;
    if (!pharmacyId || !isOpenForFetch) {
      setRefillDueList([]);
      return;
    }
    setRefillLoading(true);
    try {
      const list = await counselingService.getRefillAssessmentDueList(pharmacyId);
      setRefillDueList(list);
    } catch {
      setRefillDueList([]);
    } finally {
      setRefillLoading(false);
    }
  }, [activePharmacy?.id, isOpenForFetch]);

  useEffect(() => {
    fetchRefillDueList();
  }, [fetchRefillDueList]);

  // Reset to page 1 whenever a filter changes so users don't get stranded on an empty page.
  // Does NOT depend on page/limit themselves to avoid infinite loops.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setPage(1);
    // GP-1337: drop the selection too — a filter change swaps the visible rows,
    // and a bulk assign must never act on tasks the user can no longer see.
    setSelectedIds([]);
  }, [
    filterStatus, filterLinkedStatus, filterDueDate, searchTerm,
    createdDateFrom, createdDateTo, queueTab,
  ]);

  const toggleSort = (field: 'createdAt' | 'dueDate' | 'deliveryDate') => {
    if (sortBy === field) {
      setSortOrder(prev => (prev === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortBy(field);
      setSortOrder('DESC');
    }
    setPage(1);
  };

  // Debounce search by debouncing the COMMITTED term: the input is uncontrolled
  // (defaultValue), and the fetchTasks effect fires once when searchTerm lands,
  // with the token from that render. Scheduling fetchTasks() here is what the
  // GP-1116 token gate turned into a stale-win bug: the 400ms-late call closed
  // over the PREVIOUS render's fetchTasks (old searchTerm), took the newest
  // token, and its stale-term results clobbered the correct in-flight response
  // (Codex review finding). It also defeated the debounce — the effect already
  // fetched on every keystroke because setSearchTerm ran immediately.
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  // The search box is uncontrolled; Clear filters blanks it through this ref.
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const handleSearchChange = (value: string) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => updateFilter('searchTerm', value), 400);
  };
  useEffect(() => () => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
  }, []);

  // ---- Items ----

  const items = response?.items || [];
  // Client-side filter for derived statuses (overdue/declined are not DB statuses).
  // Hide cancelled tasks from the default 'all' view — they reappear when the user
  // explicitly selects the 'cancelled' filter (Fix 8 — see
  // 2026-04-28-alex-walid-meeting-fixes.md).
  const displayItems = filterStatus === 'overdue'
    ? items.filter(isOverdue)
    : filterStatus === 'declined'
    ? items.filter(isCounselingDeclined)
    : filterStatus === 'all'
    ? items.filter(item => item.status !== 'cancelled')
    : items;

  const stats = response?.stats;
  const counts = {
    total: stats?.total || 0,
    inProgress: stats?.inProgress ?? ((stats?.byStatus?.in_progress || 0) + (stats?.byStatus?.open || 0)),
    completed: stats?.byStatus?.completed || 0,
    overdue: stats?.overdue || 0,
  };

  // GP-1337: selection is derived against the rows currently on screen, so a
  // filter change, a page change or a refetch can never carry a stale id into a
  // bulk assign — the checkboxes and the mutation read the same list.
  const selectableIds = displayItems.filter(isAssignable).map((t) => t.id);
  const selectableIdSet = new Set(selectableIds);
  const effectiveSelectedIds = selectedIds.filter((id) => selectableIdSet.has(id));
  const effectiveSelectedIdSet = new Set(effectiveSelectedIds);
  const allSelectableSelected =
    selectableIds.length > 0 && effectiveSelectedIds.length === selectableIds.length;

  const toggleRowSelected = (taskId: string) => {
    setSelectedIds((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId],
    );
  };
  const toggleSelectAll = () => {
    setSelectedIds(allSelectableSelected ? [] : selectableIds);
  };

  // ---- Resolve auth0 IDs to display names ----

  const [auth0NameMap, setAuth0NameMap] = useState<Map<string, string>>(new Map());
  // GP-1337: auth0 IDs this view has already asked the server about. Without it
  // the effect re-fires before setAuth0NameMap lands (displayItems is a fresh
  // array every render, and auth0NameMap is a dependency), so ONE unresolved id
  // produced a burst of identical GET /api/auth/user-lookup calls — 7 of them,
  // ~1.25s each, on every /tasks load. One request per id per view now.
  const lookupRequestedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Collect auth0 IDs that aren't resolved by pharmacy users map or backend
    const unresolvedIds = new Set<string>();
    for (const item of displayItems) {
      // Check assignedTo
      const id = item.assignedTo;
      if (id && id.startsWith('auth0|') && !item.assignedToName && !pharmacyUsersData?.userIdToNameMap?.get(id) && !auth0NameMap.has(id) && !lookupRequestedRef.current.has(id)) {
        unresolvedIds.add(id);
      }
      // Check activity log user IDs
      if (item.activityLog) {
        for (const entry of item.activityLog) {
          if (entry.user && entry.user.startsWith('auth0|') && !pharmacyUsersData?.userIdToNameMap?.get(entry.user) && !auth0NameMap.has(entry.user) && !lookupRequestedRef.current.has(entry.user)) {
            unresolvedIds.add(entry.user);
          }
        }
      }
    }
    if (unresolvedIds.size === 0) return;
    // Claim these ids BEFORE awaiting so a re-render mid-flight can't re-request them.
    unresolvedIds.forEach((id) => lookupRequestedRef.current.add(id));

    // Strip auth0| prefix, look up each user via the user-lookup endpoint
    Promise.all(
      Array.from(unresolvedIds).map(async (auth0Id) => {
        try {
          const res = await api.get('/api/auth/user-lookup', { params: { auth0Id } });
          if (res.data?.success && res.data.source !== 'not_found') {
            return [auth0Id, res.data.displayName] as const;
          }
        } catch { /* skip failed lookups */ }
        return null;
      }),
    ).then((results) => {
      const newMap = new Map(auth0NameMap);
      let changed = false;
      for (const r of results) {
        if (r) { newMap.set(r[0], r[1]); changed = true; }
      }
      if (changed) setAuth0NameMap(newMap);
    });
  }, [displayItems, pharmacyUsersData, auth0NameMap]);

  const resolveUserName = (userId?: string, assignedToName?: string): string => {
    if (!userId) return 'Unassigned';
    // 1. Use backend-resolved name (PHOX Cloud MongoDB lookup)
    if (assignedToName) return assignedToName;
    // 2. Try pharmacy users map (local cache)
    if (pharmacyUsersData?.userIdToNameMap) {
      const name = pharmacyUsersData.userIdToNameMap.get(userId);
      if (name) return name;
    }
    // 3. Try frontend-resolved auth0 names
    const resolved = auth0NameMap.get(userId);
    if (resolved) return resolved;
    // 4. Fallback
    return userId.includes('@') ? userId.split('@')[0] : userId;
  };

  // ---- Assignment (GP-1337, department-aware since PRX-67 D7) ----

  /**
   * The users a task in a given department may be routed to. Derived from the
   * pharmacy-users payload this view ALREADY loads (the hook exposes
   * customRole for exactly this) — same predicate the server applies for
   * `?roles=`, so no second round-trip for a subset of data we have. Mirrors
   * ClinicalVerificationQueue's `clinicalStaffUsers`.
   *
   * `department` null/undefined offers the union of every department's roles
   * (`assignableRolesFor(null)`) — used for a department-less task and as the
   * fallback list below.
   */
  const getAssignableUsers = useCallback(
    (department?: TaskDepartment | null) => getDepartmentAssignableUsers(pharmacyUsersData?.users, department),
    [pharmacyUsersData],
  );

  // The union list — used for naming lookups and as the bulk dialog's fallback
  // when nothing (or nothing with a department) is selected.
  const assignableUsers = useMemo(() => getAssignableUsers(null), [getAssignableUsers]);

  // Only offer "Assign to Me" when the signed-in user is themselves assignable
  // for THIS department — offering an option the server will reject is worse
  // than not offering it.
  const canAssignToSelfFor = useCallback(
    (department?: TaskDepartment | null) =>
      !!currentUserEmail && getAssignableUsers(department).some((u) => u.id === currentUserEmail),
    [currentUserEmail, getAssignableUsers],
  );

  // The distinct departments among the currently-selected tasks, used by both
  // the bulk picker's intersection below and its empty-state message.
  const bulkSelectedDepartments = useMemo(() => {
    const selectedTasks = displayItems.filter((t) => effectiveSelectedIdSet.has(t.id));
    const seen = new Set<TaskDepartment | null>();
    for (const task of selectedTasks) seen.add(task.department || null);
    return Array.from(seen);
  }, [displayItems, effectiveSelectedIdSet]);

  /**
   * The bulk action bar assigns ONE value to potentially many tasks at once,
   * which can span departments. PRX-67 (wave 1 review item 4, Medium): the
   * server validates EVERY distinct department represented in the selection
   * (manual-tasks.service.ts bulkAssignTasks resolves against each one), so
   * the picker must offer only users assignable to ALL of them — an
   * INTERSECTION, not a union. The old union let staff pick a user the server
   * would then 400 on for one of the other selected tasks.
   */
  const bulkAssignableUsers = useMemo(() => {
    if (bulkSelectedDepartments.length === 0) return assignableUsers;
    const [firstDept, ...restDepts] = bulkSelectedDepartments;
    let intersection = getAssignableUsers(firstDept);
    for (const dept of restDepts) {
      const allowedIds = new Set(getAssignableUsers(dept).map((u) => u.id));
      intersection = intersection.filter((u) => allowedIds.has(u.id));
    }
    return intersection;
  }, [bulkSelectedDepartments, getAssignableUsers, assignableUsers]);

  /**
   * Assign the given tasks, or unassign when `value` is empty. Shared by the
   * inline row picker and the bulk action bar so both go through one validated
   * endpoint. Errors are surfaced verbatim — the server rejects an assignee whose
   * role can't conduct counseling, and the user needs to see why.
   */
  const assignTasks = useCallback(async (taskIds: string[], value: string): Promise<boolean> => {
    if (!activePharmacy?.id || taskIds.length === 0) return false;
    const assignedTo = value === 'me'
      ? (currentUserEmail || null)
      : (value ? value : null);
    if (value === 'me' && !assignedTo) {
      setSnackbar({ open: true, message: 'Could not determine your user account', severity: 'error' });
      return false;
    }
    setAssigning(true);
    try {
      await bulkAssignTasks(activePharmacy.id, taskIds, assignedTo);
      const name = assignedTo
        ? (assignableUsers.find((u) => u.id === assignedTo)?.name || assignedTo)
        : '';
      setSnackbar({
        open: true,
        message: assignedTo
          ? `${taskIds.length} task(s) assigned to ${name}`
          : `${taskIds.length} task(s) unassigned`,
        severity: 'success',
      });
      fetchTasks();
      // PRX-67: an assignment can move a task into/out of the Mine bucket —
      // refresh the tab chips and sidebar badge rather than wait out the 30s staleTime.
      invalidateCounts();
      return true;
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || err?.message || 'Failed to assign task(s)',
        severity: 'error',
      });
      return false;
    } finally {
      setAssigning(false);
    }
  }, [activePharmacy?.id, currentUserEmail, assignableUsers, fetchTasks, invalidateCounts]);

  // Stable identity so the memoized AssignedToCell can actually skip re-renders.
  // This table is NOT virtualized — up to 500 rows each mount a full Autocomplete,
  // so an unstable callback would re-render every one of them on each parent state
  // change (search debounce, selection tick, dialog open).
  const handleRowAssignmentChange = useCallback((taskId: string, value: string) => {
    void assignTasks([taskId], value);
  }, [assignTasks]);

  /**
   * Detail-pane variant. `detailView` holds a SNAPSHOT of the task, so it has to
   * be patched locally too — fetchTasks() refreshes the list behind the pane, not
   * this object, and the picker would otherwise snap back to Unassigned.
   */
  const handleDetailAssignmentChange = async (taskId: string, value: string) => {
    const ok = await assignTasks([taskId], value);
    if (!ok) return;
    const assignedTo = value === 'me' ? (currentUserEmail || undefined) : (value || undefined);
    setDetailView((prev) =>
      prev && prev.id === taskId
        // assignedToName is dropped so resolveUserName re-derives it from the
        // pharmacy-users map instead of showing the previous assignee's name.
        ? { ...prev, assignedTo, assignedToName: undefined }
        : prev,
    );
  };

  const handleBulkAssign = async () => {
    if (!assignTargetUser) {
      setSnackbar({ open: true, message: 'Please select a user', severity: 'warning' });
      return;
    }
    const ok = await assignTasks(effectiveSelectedIds, assignTargetUser);
    if (ok) {
      setAssignDialogOpen(false);
      setAssignTargetUser('');
      setSelectedIds([]);
    }
  };

  // ---- Handlers ----

  const handleCreateTask = () => {
    setEditingTask(null);
    setTaskDialog(true);
  };

  const handleEditTask = (task: TaskQueueItem) => {
    if (task.type !== 'manual' && task.type !== 'counseling') return;
    setEditingTask(task);
    setTaskDialog(true);
  };

  const handleSaveTask = async (dto: any) => {
    if (!activePharmacy?.id) return;
    setSaving(true);
    try {
      if (editingTask) {
        await updateManualTask(editingTask.id, dto);
        setSnackbar({ open: true, message: 'Task updated', severity: 'success' });
      } else if (dto.taskType === 'patient_counselling') {
        // GP-1115: a Patient Counselling task is created through the counseling
        // pipeline, not as a bare manual task. That gives it a real counseling
        // record (visible in Counseling History, drives the GP-1114 status chip)
        // and source='counseling', so the queue offers the Start Counseling flow
        // instead of a documentation-less generic Complete.
        // GP-1222: POST body extracted to counselingService.createCounselingTask
        // so the Patient 360 Order History "Add Counseling Task" action shares it.
        await counselingService.createCounselingTask(activePharmacy.id, dto);
        setSnackbar({ open: true, message: 'Counseling task created', severity: 'success' });
      } else {
        await createManualTask(activePharmacy.id, dto);
        setSnackbar({ open: true, message: 'Task created', severity: 'success' });
      }
      setTaskDialog(false);
      fetchTasks();
      // PRX-67: a new task lands in a department bucket immediately.
      invalidateCounts();
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.message || 'Failed to save task', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteTask = async (task: TaskQueueItem, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (task.linkStatus === 'unlinked') return;
    // Show confirmation dialog before completing
    setTaskToComplete(task);
    setConfirmCompleteOpen(true);
  };

  const handleConfirmComplete = async () => {
    if (!taskToComplete) return;
    setConfirmCompleteOpen(false);
    try {
      await updateManualTask(taskToComplete.id, { status: 'completed' });
      setSnackbar({ open: true, message: 'Task completed', severity: 'success' });
      setTaskToComplete(null);
      fetchTasks();
      // PRX-67: a closed task drops out of every open-task count immediately.
      invalidateCounts();
    } catch {
      setSnackbar({ open: true, message: 'Failed to complete task', severity: 'error' });
      setTaskToComplete(null);
    }
  };

  const handleStartCounselingFromTable = (task: TaskQueueItem, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setDetailView(task);
    handleStartCounseling(task);
  };

  // ---- GP-1488: /tasks?taskId=<id> deep link ----
  //
  // Clinical Verification links straight to the counseling task its ISEC answers
  // came from. Fetch that ONE task by id rather than hunting for it in the
  // current page — the queue's filters are persisted per user (GP-1337), so a
  // saved Status/Due Date view would otherwise silently swallow the link. The
  // param is consumed once and stripped so Back doesn't re-open the detail.
  const deepLinkedRef = useRef<string | null>(null);
  useEffect(() => {
    // PRX-105: an embedded, locked queue is not the /tasks page and owns no deep link.
    if (!isPage || department) return;
    // The detail read is pharmacy-scoped (multi-tenant rule) — wait for the
    // active pharmacy to hydrate before consuming the deep link.
    if (!activePharmacy?.id) return;
    const taskId = new URLSearchParams(window.location.search).get('taskId');
    if (!taskId || deepLinkedRef.current === taskId) return;
    deepLinkedRef.current = taskId;
    let cancelled = false;
    getManualTaskDetail(activePharmacy.id, taskId)
      .then((res) => {
        if (cancelled || !res?.item) return;
        setDetailView(res.item);
      })
      .catch(() => {
        if (!cancelled) {
          setSnackbar({
            open: true,
            message: 'That task could not be opened — it may have been deleted',
            severity: 'warning',
          });
        }
      })
      .finally(() => {
        if (!cancelled) navigate('/tasks', { replace: true });
      });
    return () => {
      cancelled = true;
    };
  }, [isPage, department, navigate, activePharmacy?.id]);

  const handleAddNote = async () => {
    if (!detailView || !noteText.trim()) return;
    try {
      await addTaskNote(detailView.id, noteText.trim());
      setNoteText('');
      setSnackbar({ open: true, message: 'Note added', severity: 'success' });
      fetchTasks();
    } catch {
      setSnackbar({ open: true, message: 'Failed to add note', severity: 'error' });
    }
  };

  // ---- Counseling Session Handler ----

  const handleStartCounseling = async (task: TaskQueueItem) => {
    const pharmacyId = activePharmacy?.id;
    // Claim the latest-click token up front; any earlier in-flight invocation is
    // now stale and its late responses will be ignored below (GP-1081).
    const requestToken = ++counselingRequestRef.current;
    const isStale = () => counselingRequestRef.current !== requestToken;
    setCounselingExistingRecord(null);
    setCounselingOrderData(null);
    if (!pharmacyId) {
      setCounselingDialogOpen(true);
      return;
    }
    if (!task.orderId) {
      // GP-1115: manually created counseling tasks have no order — build the
      // dialog context from the task and its linked counseling record instead
      // of fetching an order. Without this, the dialog's render gate
      // (counselingOrderData) never opened for order-less tasks.
      setCounselingLoading(true);
      try {
        const res = await api.get(`/api/counseling/by-task/${task.id}`);
        const rec = res.data?.data || null;
        if (isStale()) return;
        setCounselingExistingRecord(rec);
        setCounselingOrderData({
          pharmacyId,
          orderId: null,
          orderNumber: task.orderNumber || null,
          rxNumber: task.rxNumber ?? rec?.rxNumber ?? null,
          din: rec?.din ?? null,
          drugName: task.medication || rec?.drugName || null,
          patientName: task.patient?.name || rec?.patientName || '',
          patientId: task.patient?.id || rec?.patientId || null,
          doctorName: rec?.doctorName || null,
        });
      } catch {
        if (!isStale()) {
          // Record lookup failed — still open with task-only context so the
          // pharmacist can document the session; finalize creates a record.
          setCounselingOrderData({
            pharmacyId,
            orderId: null,
            orderNumber: task.orderNumber || null,
            rxNumber: task.rxNumber ?? null,
            din: null,
            drugName: task.medication || null,
            patientName: task.patient?.name || '',
            patientId: task.patient?.id || null,
            doctorName: null,
          });
        }
      } finally {
        if (!isStale()) {
          setCounselingLoading(false);
          setCounselingDialogOpen(true);
        }
      }
      return;
    }
    setCounselingLoading(true);
    try {
      const orderRes = await api.get(`/api/orders/${pharmacyId}/${task.orderId}`);
      const order = orderRes.data?.data || orderRes.data;
      // Prefer the task's rxNumber — it's drug-specific. The order's rxNumber is
      // just the order's primary rx, so for multi-drug orders it resolves every
      // counseling task to the same (first) drug.
      const rxNumber = task.rxNumber || order?.rxNumber;
      const isTaskSpecificRx = !!task.rxNumber;

      // Hardcopy (Kroll may be unavailable) and the GP-928 resume lookup are
      // independent — fetch them in parallel.
      const hardcopyPromise: Promise<any> = rxNumber
        ? api.get(`/api/rx/${pharmacyId}/hardcopy/${rxNumber}`)
            .then((hcRes) => hcRes.data?.data || hcRes.data)
            .catch(() => null)
        : Promise.resolve(null);
      // GP-928: resume a saved-for-later session — load the open counseling
      // record so its checklist/status prefill the dialog instead of opening
      // blank (the backend already dedups on save; the UI must show that work).
      const openRecordPromise: Promise<any> = api
        .get(`/api/counseling/by-order/${pharmacyId}/${task.orderId}`)
        .then((recRes) => {
          const recs: any[] = recRes.data?.data || recRes.data || [];
          const openRecs = recs.filter((r) => r.status === 'pending' || r.status === 'accepted');
          const linkedId = task.metadata?.counselingRecordId;
          const byTaskLink = linkedId ? openRecs.find((r) => r.id === linkedId) : undefined;
          const byRx = rxNumber
            ? openRecs.find((r) => r.rxNumber != null && String(r.rxNumber) === String(rxNumber))
            : undefined;
          return byTaskLink || byRx || null;
        })
        .catch(() => null); // resume prefill is best-effort
      const [hardcopy, openRecord] = await Promise.all([hardcopyPromise, openRecordPromise]);
      // A newer Start Counseling click superseded this one — drop these results
      // so we never bind a prior order's drug/record into the open panel.
      if (isStale()) return;
      setCounselingExistingRecord(openRecord);

      // When the task points at a specific rx that differs from the order's primary,
      // don't fall back to order-level drug fields — they describe the wrong drug.
      const taskDrugName = extractDrugNameFromDescription(task.description);
      const orderDrugBrand = isTaskSpecificRx ? undefined : order?.medicationName;
      const orderDin = isTaskSpecificRx ? undefined : order?.din;
      const orderQuantity = isTaskSpecificRx ? undefined : order?.quantity;
      const orderDaysSupply = isTaskSpecificRx ? undefined : order?.daysSupply;

      setCounselingOrderData({
        pharmacyId,
        orderId: task.orderId,
        orderNumber: order?.orderNumber,
        rxNumber: rxNumber || undefined,
        patientName: order?.patientName || task.patient?.name,
        patientId: order?.patientId || task.patient?.id,
        patientDob: hardcopy?.patientDOB || order?.metadata?.patientDob,
        patientSex: hardcopy?.patientSex || order?.metadata?.patientSex,
        allergies: order?.metadata?.allergies,
        conditions: order?.metadata?.conditions,
        drugBrand: hardcopy?.brandName || orderDrugBrand || taskDrugName,
        drugGeneric: hardcopy?.genericName,
        din: hardcopy?.drugDIN || orderDin,
        form: hardcopy?.drugForm,
        strength: hardcopy?.drugStrength,
        quantity: hardcopy?.dispQty || orderQuantity,
        daysSupply: hardcopy?.daysSupply || orderDaysSupply,
        sig: hardcopy?.sigExpanded || hardcopy?.sig,
        doctorName: hardcopy?.doctorName || order?.metadata?.doctorName,
        doctorLicense: hardcopy?.doctorLicence || order?.metadata?.doctorLicense,
        doctorPhone: order?.metadata?.doctorPhone,
        patientPhone: hardcopy?.patientPhone || order?.patientPhone || undefined,
        patientAddress: hardcopy?.patientAddress
          ? [hardcopy.patientAddress, hardcopy.patientAddress2].filter(Boolean).join(', ')
          : undefined,
        deliveryDate: order?.deliveryDate,
        deliveryType: order?.deliveryType,
      });
    } catch {
      if (isStale()) return;
      const desc = task.description || '';
      const drugMatch = desc.match(/Drug:\s+(.+?)\./);
      const dinMatch = desc.match(/DIN:\s+(\d+)\./);
      setCounselingOrderData({
        pharmacyId,
        orderId: task.orderId,
        patientName: task.patient?.name,
        patientId: task.patient?.id,
        drugBrand: drugMatch?.[1],
        din: dinMatch?.[1],
      });
    } finally {
      // Only the latest click may clear the spinner and open the panel; a stale
      // invocation must leave the current session untouched.
      if (!isStale()) {
        setCounselingLoading(false);
        setCounselingDialogOpen(true);
      }
    }
  };

  const handleDeclineCounselingTask = async (task: TaskQueueItem) => {
    const pharmacyId = activePharmacy?.id;
    if (!pharmacyId) return;

    const effectiveReason = declineReason === 'other'
      ? (declineReasonOther.trim() ? `Other: ${declineReasonOther.trim()}` : '')
      : declineReason;

    if (!effectiveReason) {
      setSnackbar({ open: true, message: 'Please select a reason for declining', severity: 'error' });
      return;
    }

    setDecliningCounseling(true);
    try {
      // If the task has an associated counseling record ID in metadata, update it directly
      const counselingRecordId = task.metadata?.counselingRecordId;
      if (counselingRecordId) {
        await api.patch(`/api/counseling/${pharmacyId}/${counselingRecordId}`, {
          status: 'declined',
          declinedReason: effectiveReason,
        });
        await api.post(`/api/counseling/${pharmacyId}/${counselingRecordId}/finalize`);
      } else {
        // Create a new declined counseling record
        const { data } = await api.post('/api/counseling', {
          pharmacyId,
          orderId: task.orderId,
          rxNumber: task.rxNumber,
          patientId: task.patient?.id,
          patientName: task.patient?.name,
          drugName: task.description?.match(/Drug:\s+(.+?)\./)?.[1],
          status: 'declined',
          reason: 'new_rx',
          declinedReason: effectiveReason,
          counselingOrigin: 'task_queue',
        });
        const recordId = data?.data?.id;
        if (recordId) {
          await api.post(`/api/counseling/${pharmacyId}/${recordId}/finalize`);
        }
      }
      // Mark the task as completed with decline reason in description
      try {
        await updateManualTask(task.id, {
          status: 'completed',
          description: `Counseling declined — Reason: ${effectiveReason}`,
        });
      } catch (taskErr: any) {
        console.warn('Failed to complete task after declining counseling:', taskErr.message);
      }

      setSnackbar({ open: true, message: 'Counseling declined and task completed', severity: 'success' });
      setDeclineCounselingOpen(false);
      setDeclineReason('');
      setDeclineReasonOther('');
      setDetailView(null);
      fetchTasks();
      // PRX-67 (wave 1 review item 5, Medium) — this completes the task, which
      // moves it out of the open-count tallies the tabs/badge show; without
      // this the department chip and the sidebar badge stay stale until the
      // next unrelated refresh.
      invalidateCounts();
    } catch (err: any) {
      setSnackbar({ open: true, message: `Failed to decline: ${err.message}`, severity: 'error' });
    } finally {
      setDecliningCounseling(false);
    }
  };

  // ---- Outreach attempt / snooze (GP-1365) ----

  // "Today" here is the PHARMACY's today (America/Toronto), not the browser's —
  // the server validates the due date against its own calendar day, so a user in
  // another timezone would otherwise be offered dates the API rejects. All the
  // date rules live in ./outreachSnooze as pure, unit-tested string functions.
  const todayString = getPharmacyTodayString();
  const outreachMaxDate = snoozeCeiling(detailView?.deliveryDate, todayString);
  // The earliest date that actually clears the Overdue bucket. Due-TODAY still
  // counts as overdue on the row badge AND in the stats tile, so offering it
  // would let the counselor save, be told "Outreach logged", and find the task
  // exactly where it was.
  const outreachMinDate = snoozeFloor(todayString);
  // Delivery today puts the ceiling below the floor: nothing is schedulable.
  const outreachHasLegalDate = hasLegalSnoozeDate(todayString, outreachMaxDate);

  const openOutreachDialog = () => {
    setOutreachOutcome('');
    setOutreachNote('');
    setOutreachDueDate(defaultSnoozeDueDate(todayString, outreachMaxDate));
    setOutreachOpen(true);
  };

  const outreachDueDateError = validateSnoozeDueDate(
    outreachDueDate,
    todayString,
    outreachMaxDate,
  );

  const handleLogOutreach = async () => {
    // Mirrors the Save button's disabled conditions exactly, so the handler can
    // never fire for a date the validator (or the server) would reject — including
    // an empty seed when the delivery is today and no legal date exists.
    if (
      !detailView || !activePharmacy?.id || !outreachOutcome
      || !outreachDueDate || !!outreachDueDateError || !outreachHasLegalDate
    ) return;
    setLoggingOutreach(true);
    try {
      const { item } = await logOutreachAttempt(activePharmacy.id, detailView.id, {
        outcome: outreachOutcome,
        dueDate: outreachDueDate,
        note: outreachNote.trim() || undefined,
      });
      // Patch the detail SNAPSHOT from the persisted item — Due Date, the attempt
      // count and the Activity Log then show what actually landed rather than an
      // optimistic guess (fetchTasks refreshes the list behind the pane, not this).
      setDetailView((prev) => (prev && prev.id === item.id ? { ...prev, ...item } : prev));
      setOutreachOpen(false);
      setSnackbar({
        open: true,
        message: `Outreach logged — due date moved to ${formatTaskDate(outreachDueDate, 'MMM dd, yyyy')}`,
        severity: 'success',
      });
      fetchTasks();
    } catch (err: any) {
      // A class-validator DTO failure returns `message` as a string[]; rendering it
      // straight gives the counselor a comma-mashed blob. Join it readably instead.
      const detail = err?.response?.data?.message;
      const reason = Array.isArray(detail) ? detail.join('; ') : detail;
      setSnackbar({
        open: true,
        message: reason || err?.message || 'Failed to log outreach attempt',
        severity: 'error',
      });
    } finally {
      setLoggingOutreach(false);
    }
  };

  const handleReopenCounseling = async (task: TaskQueueItem) => {
    const pharmacyId = activePharmacy?.id;
    if (!pharmacyId) return;

    setReopeningCounseling(true);
    try {
      await api.post(`/api/counseling/${pharmacyId}/reopen-by-task/${task.id}`);
      setSnackbar({ open: true, message: 'Counseling reopened — task moved back to In Progress', severity: 'success' });
      setDetailView(null);
      fetchTasks();
    } catch (err: any) {
      setSnackbar({ open: true, message: `Failed to reopen: ${err.message}`, severity: 'error' });
    } finally {
      setReopeningCounseling(false);
    }
  };

  // ---- Detail View ----

  const renderDetailView = () => {
    if (!detailView) return null;
    const statusDisplay = getStatusDisplay(detailView);
    const linkDisplay = getLinkStatusDisplay(detailView.linkStatus);

    return (
      <Box sx={{ height: isPage ? 'calc(100vh - 220px)' : '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'background.paper' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => { setDetailView(null); setNoteText(''); }}
            size="small"
            sx={{ textTransform: 'none', mr: 'auto' }}
          >
            Back to list
          </Button>
          {(detailView.type === 'manual' || detailView.type === 'counseling') && (
            <IconButton size="small" onClick={() => { handleEditTask(detailView); setDetailView(null); }}>
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          {onClose && (
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {/* Title Card */}
          <Paper sx={{ p: 0, mb: 2, borderRadius: 2, overflow: 'hidden', border: 1, borderColor: 'divider' }}>
            <Box sx={{ px: 2, py: 1.5, bgcolor: statusDisplay.color === 'success' ? 'success.main' : statusDisplay.color === 'error' ? 'error.main' : statusDisplay.color === 'default' ? 'grey.600' : 'primary.main', color: 'white' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Chip
                  label={getTaskTypeLabel(detailView.taskType)}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600, fontSize: '0.75rem' }}
                />
                <Chip
                  label={statusDisplay.label}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 700 }}
                />
              </Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                {detailView.title}
              </Typography>
            </Box>
            {detailView.description && (
              <Box sx={{ px: 2, py: 1, bgcolor: headerStripSurface, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>{detailView.description}</Typography>
              </Box>
            )}
          </Paper>

          {/* Patient & Prescription Info */}
          {(detailView.patient?.name || detailView.rxNumber) && (
            <Paper sx={{ p: 2, mb: 2, borderRadius: 2, border: 1, borderColor: 'divider' }}>
              <Grid container spacing={2}>
                {detailView.patient?.name && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.05em' }}>Patient</Typography>
                    {detailView.patient.id && activePharmacy?.id ? (
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline', '&:hover': { color: 'primary.dark' } }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const pid = detailView.patient!.id!;
                          const compositeId = pid.includes('_') ? pid : `${activePharmacy.id}_${pid}`;
                          navigate(`/patients/${compositeId}`);
                        }}
                      >
                        {detailView.patient.name}
                      </Typography>
                    ) : (
                      <Typography variant="body2" fontWeight={700}>{detailView.patient.name}</Typography>
                    )}
                    {detailView.patientDob && <Typography variant="caption" color="text.secondary">DOB: {detailView.patientDob}</Typography>}
                    {detailView.krollId && <Typography variant="caption" color="text.secondary" display="block">Kroll: {detailView.krollId}</Typography>}
                  </Grid>
                )}
                {detailView.rxNumber && (
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.05em' }}>Rx #</Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>{detailView.rxNumber}</Typography>
                    {(() => {
                      const drug = extractDrugNameFromDescription(detailView.description);
                      return drug ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {drug}
                        </Typography>
                      ) : null;
                    })()}
                  </Grid>
                )}
                {detailView.orderNumber && (
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.05em' }}>Order #</Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem', wordBreak: 'break-all' }}>{detailView.orderNumber}</Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          )}

          {/* PRX-132: a task made from a Communication Center thread links back to it */}
          <SourceThreadLink sourceThreadId={detailView.sourceThreadId} />

          {/* Task Details Grid */}
          <Paper sx={{ p: 0, mb: 2, borderRadius: 2, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
            {[
              { label: 'Entry Method', value: detailView.entryMethod || '—' },
              { label: 'Troubleshooting Reason', value: detailView.troubleshootingReason || '—' },
              // GP-1337: editable here too — the detail pane used to render the
              // same dead "Unassigned" text as the list.
              {
                label: 'Assigned To',
                value: resolveUserName(detailView.assignedTo, detailView.assignedToName),
                assignee: true,
              },
              ...(detailView.dueDate ? [{ label: 'Due Date', value: format(parseISO(detailView.dueDate + 'T00:00:00'), 'MMM dd, yyyy'), color: isOverdue(detailView) ? 'error.main' : undefined }] : []),
              // GP-1365: how many times we have already tried to reach this
              // patient. Turns amber at the escalation threshold so a task that
              // keeps getting deferred stops looking routine.
              ...(detailView.outreachAttempts ? [{
                label: 'Outreach Attempts',
                value: String(detailView.outreachAttempts),
                color: detailView.outreachAttempts >= OUTREACH_ESCALATION_THRESHOLD ? 'warning.main' : undefined,
              }] : []),
              { label: 'Link Status', value: linkDisplay.label, chip: true, chipColor: linkDisplay.color, chipVariant: 'outlined' as const },
              ...(detailView.deliveryDate ? [{ label: 'Delivery Date', value: formatTaskDate(detailView.deliveryDate, 'MMM dd, yyyy') }] : []),
              { label: 'Created', value: detailView.createdAt ? format(parseISO(detailView.createdAt), 'MMM dd, yyyy h:mm a') : '—' },
            ].map((row, i) => (
              <Box
                key={row.label}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: 2,
                  py: 1,
                  bgcolor: i % 2 === 0 ? 'background.paper' : headerStripSurface,
                  borderBottom: 1,
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 0 },
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, mr: 2 }}>{row.label}</Typography>
                {(row as any).assignee ? (
                  <AssignedToCell
                    rowId={detailView.id}
                    assignedTo={detailView.assignedTo}
                    availableUsers={getAssignableUsers(detailView.department)}
                    userIdToNameMap={pharmacyUsersData?.userIdToNameMap}
                    onAssignmentChange={handleDetailAssignmentChange}
                    includeAssignToMe={canAssignToSelfFor(detailView.department)}
                    disabled={assigning || !isAssignable(detailView)}
                    minWidth={240}
                    noOptionsText={noAssignableUsersText(detailView.department)}
                  />
                ) : (row as any).chip ? (
                  <Chip label={row.value} size="small" color={(row as any).chipColor} variant={(row as any).chipVariant} />
                ) : (
                  <Typography variant="body2" fontWeight={500} sx={{ textAlign: 'right', color: (row as any).color || 'text.primary' }}>{row.value}</Typography>
                )}
              </Box>
            ))}
          </Paper>

          {/* GP-368: the resolution pathway configured for this Troubleshooting
              Reason. Renders nothing for reasons with no configured steps.
              BILLING tasks only — the playbooks are billing SLA content, and
              reasons like Follow Up With Patient also exist at Clinical
              Verification, where "re-bill the claim" instructions would be
              wrong (review, 2026-08-13). */}
          {detailView.type === 'troubleshooting_billing' && (
            <TroubleshootingStepsPanel reason={detailView.troubleshootingReason} />
          )}

          {/* GP-1203: superseded by a rebuilt Rx — explain instead of offering actions */}
          {isCounselingSuperseded(detailView) && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              <Typography variant="body2" fontWeight={600}>Superseded</Typography>
              <Typography variant="body2">{supersededByLabel(detailView)}</Typography>
            </Alert>
          )}

          {/* Counseling action buttons */}
          {detailView.type === 'counseling' && detailView.status !== 'completed' && !isCounselingSuperseded(detailView) && (
            <Paper sx={{ p: 2, mb: 2, borderRadius: 2, border: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={counselingLoading}
                  onClick={() => handleStartCounseling(detailView)}
                  sx={{ textTransform: 'none', borderRadius: 1.5 }}
                >
                  {counselingLoading ? <CircularProgress size={20} /> : 'Start Counseling Session'}
                </Button>
                {/* GP-1365: the third path. "Called, left a voicemail, will retry
                    Thursday" is neither completing the session nor declining it —
                    without this the task just ages into Overdue with no record of
                    the outreach. */}
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  size="small"
                  startIcon={<OutreachIcon />}
                  onClick={openOutreachDialog}
                  sx={{ textTransform: 'none', borderRadius: 1.5 }}
                >
                  Log Outreach Attempt
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  fullWidth
                  size="small"
                  startIcon={<DeclineIcon />}
                  onClick={() => setDeclineCounselingOpen(!declineCounselingOpen)}
                  sx={{ textTransform: 'none', borderRadius: 1.5 }}
                >
                  Decline Counseling
                </Button>
                {declineCounselingOpen && (
                  <Box sx={{ p: 1.5, bgcolor: 'rgba(255, 193, 7, 0.08)', borderRadius: 1.5, border: 1, borderColor: 'warning.light' }}>
                    <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                      <InputLabel>Reason</InputLabel>
                      <Select
                        value={declineReason}
                        label="Reason"
                        onChange={(e) => {
                          setDeclineReason(e.target.value);
                          if (e.target.value !== 'other') setDeclineReasonOther('');
                        }}
                      >
                        {DECLINED_REASONS.map((r) => (
                          <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {declineReason === 'other' && (
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Specify reason..."
                        value={declineReasonOther}
                        onChange={(e) => setDeclineReasonOther(e.target.value)}
                        sx={{ mb: 1 }}
                      />
                    )}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => handleDeclineCounselingTask(detailView)}
                        disabled={decliningCounseling || !declineReason}
                        sx={{ borderRadius: 1.5 }}
                      >
                        {decliningCounseling ? <CircularProgress size={16} /> : 'Confirm Decline'}
                      </Button>
                      <Button
                        size="small"
                        color="inherit"
                        onClick={() => { setDeclineCounselingOpen(false); setDeclineReason(''); setDeclineReasonOther(''); }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            </Paper>
          )}

          {/* Activity Log */}
          <Paper sx={{ p: 0, mb: 2, borderRadius: 2, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1, bgcolor: headerStripSurface, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignmentIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="subtitle2" fontWeight={700}>Activity Log</Typography>
              {detailView.activityLog && detailView.activityLog.length > 0 && (
                <Chip label={detailView.activityLog.length} size="small" sx={{ height: 20, fontSize: '0.75rem', fontWeight: 600 }} />
              )}
            </Box>
            <Box sx={{ px: 2, py: 1 }}>
              {detailView.activityLog && detailView.activityLog.length > 0 ? (
                [...detailView.activityLog].reverse().map((entry, i) => (
                  <Box
                    key={i}
                    sx={{
                      py: 1,
                      borderBottom: i < detailView.activityLog!.length - 1 ? 1 : 0,
                      borderColor: 'divider',
                      display: 'flex',
                      gap: 1.5,
                    }}
                  >
                    {/* Timeline dot */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.5 }}>
                      <Box sx={{
                        width: 8, height: 8, borderRadius: '50%',
                        bgcolor: entry.action === 'created' ? 'primary.main' : entry.action === 'note' ? 'info.main' : 'success.main',
                      }} />
                      {i < detailView.activityLog!.length - 1 && (
                        <Box sx={{ width: 1, flex: 1, bgcolor: 'divider', mt: 0.5 }} />
                      )}
                    </Box>
                    {/* Content */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
                        <Typography variant="caption" fontWeight={600} color="text.primary">{formatActivityAction(entry.action)}</Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem', flexShrink: 0 }}>
                          {entry.timestamp ? format(parseISO(entry.timestamp), 'MMM dd h:mm a') : ''}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {resolveUserName(entry.user)}
                      </Typography>
                      {entry.note && (
                        <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic', color: 'text.secondary', bgcolor: headerStripSurface, px: 1, py: 0.5, borderRadius: 1, fontSize: '0.8rem' }}>
                          {entry.note}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.disabled" sx={{ py: 2, textAlign: 'center' }}>No activity yet</Typography>
              )}
            </Box>
          </Paper>

          {/* Reopen button — for declined counseling tasks */}
          {isCounselingDeclined(detailView) && (
            <Button
              variant="contained"
              color="primary"
              fullWidth
              startIcon={reopeningCounseling ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
              disabled={reopeningCounseling}
              onClick={() => handleReopenCounseling(detailView)}
              sx={{ mb: 2, borderRadius: 1.5, textTransform: 'none' }}
            >
              Reopen Counseling
            </Button>
          )}

          {/* Complete/Acknowledge button — only for non-counseling tasks;
              counseling tasks complete via the counseling session. R10: the
              label is "Acknowledge" for a task the system raised (source !==
              'manual') and "Complete" for one a person created — both call the
              same completed transition. */}
          {detailView.status !== 'completed' && detailView.type === 'manual' && (
            <Button
              variant="contained"
              color="success"
              fullWidth
              startIcon={<CheckCircleIcon />}
              disabled={detailView.linkStatus === 'unlinked'}
              onClick={() => handleCompleteTask(detailView)}
              sx={{ mb: 2, borderRadius: 1.5 }}
            >
              {getTaskCloseLabel(detailView)}
            </Button>
          )}
        </Box>

        {/* Add Note (pinned to bottom) */}
        <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }} display="flex" gap={1}>
          <TextField
            size="small"
            fullWidth
            placeholder="Add a note..."
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddNote(); } }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <IconButton onClick={handleAddNote} disabled={!noteText.trim()} color="primary" sx={{ bgcolor: noteText.trim() ? 'primary.main' : 'transparent', color: noteText.trim() ? 'white' : 'action.disabled', borderRadius: 2, '&:hover': { bgcolor: 'primary.dark' } }}>
            <SendIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    );
  };

  // ---- Refill Assessment View (GP-927) ----
  // Read-only advisory list: one row per (patient, drug) whose latest completed
  // assessment is past the interval. Patient name links to Patient 360. No
  // actions — these can be let-pass and called in bulk later.
  const renderRefillAssessmentView = () => (
    <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'rgba(237,108,2,0.06)', display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningIcon sx={{ fontSize: 18, color: REFILL_ACCENT }} />
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Patients whose most recent completed assessment is past the reassessment interval. Advisory only — these can be let pass and called in bulk later.
        </Typography>
      </Box>

      {refillLoading && (
        <Box display="flex" justifyContent="center" p={3}><CircularProgress size={24} /></Box>
      )}

      {!refillLoading && refillDueList.length === 0 && (
        <Box p={4} textAlign="center">
          <Typography variant="body2" color="textSecondary">No patients are due for reassessment</Typography>
        </Box>
      )}

      {!refillLoading && refillDueList.length > 0 && (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0 }}>
          <Table
            size="small"
            stickyHeader
            sx={{
              '& .MuiTableCell-root': { py: 0.75, px: 1 },
              '& .MuiTableCell-head': { bgcolor: 'rgba(237,108,2,0.12)', color: REFILL_ACCENT, fontWeight: 700, fontSize: '0.72rem', whiteSpace: 'nowrap' },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Patient</TableCell>
                <TableCell>Drug</TableCell>
                <TableCell>DIN</TableCell>
                <TableCell>Last Assessed</TableCell>
                <TableCell align="right">Days Since</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {refillDueList.map((item, idx) => (
                <TableRow key={`${item.patientId}-${item.din ?? idx}`} hover>
                  <TableCell>
                    {item.patientId && activePharmacy?.id ? (
                      <Typography
                        variant="body2"
                        fontSize="0.78rem"
                        fontWeight={500}
                        sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline', '&:hover': { color: 'primary.dark' } }}
                        onClick={() => {
                          const pid = item.patientId;
                          const compositeId = pid.includes('_') ? pid : `${activePharmacy.id}_${pid}`;
                          navigate(`/patients/${compositeId}`);
                        }}
                      >
                        {item.patientName || '—'}
                      </Typography>
                    ) : (
                      <Typography variant="body2" fontSize="0.78rem" fontWeight={500}>{item.patientName || '—'}</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>
                    <Tooltip title={item.drugName || ''} placement="top">
                      <Typography variant="body2" fontSize="0.78rem" noWrap sx={{ maxWidth: 220 }}>{item.drugName || '—'}</Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontSize="0.78rem" sx={{ fontFamily: 'monospace' }}>{item.din || '—'}</Typography>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" fontSize="0.78rem">{format(parseISO(item.lastAssessedAt), 'MMM d, yyyy')}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${item.daysSinceLastAssessment}d`}
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'rgba(237,108,2,0.14)', color: REFILL_ACCENT, fontWeight: 700 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  // ---- List View (Wireframe Layout) ----

  const renderListView = () => (
    <Box sx={{ height: isPage ? 'calc(100vh - 220px)' : '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant={isPage ? 'h5' : 'h6'}>Task Queue</Typography>
        <Box display="flex" gap={0.5}>
          <IconButton size="small" onClick={() => { fetchTasks(); fetchRefillDueList(); }} disabled={loading}><RefreshIcon fontSize="small" /></IconButton>
          <IconButton size="small" onClick={handleCreateTask} data-testid="task-queue-create" aria-label="Create new task"><AddIcon fontSize="small" /></IconButton>
          {onClose && (
            <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mx: 2, mt: 1 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {/* PRX-67 (D5, R4, R5): All / Billing / Clinical / Logistics / Blister
          pack / Mine tabs with live open-task counts. Replaces the old
          counseling-only hardcode — the whole task list is now navigable. */}
      {!department && (
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <TaskQueueTabs
            counts={taskQueueCounts}
            value={queueTab}
            onChange={(next) => updateFilter('queueTab', next)}
          />
        </Box>
      )}

      {/* Stats row */}
      <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box textAlign="center">
          <Typography variant="h6" color="primary.main">{counts.total}</Typography>
          <Typography variant="caption" color="textSecondary">Total</Typography>
        </Box>
        <Box textAlign="center">
          <Typography variant="h6" sx={{ color: '#ed6c02' }}>{counts.inProgress}</Typography>
          <Typography variant="caption" color="textSecondary">In Progress</Typography>
        </Box>
        <Box textAlign="center">
          <Typography variant="h6" color="success.main">{counts.completed}</Typography>
          <Typography variant="caption" color="textSecondary">Complete</Typography>
        </Box>
        <Box textAlign="center">
          <Typography variant="h6" color="error.main">{counts.overdue}</Typography>
          <Typography variant="caption" color="textSecondary">Overdue</Typography>
        </Box>
        {/* GP-927: Refill Assessment tile doubles as a tab — click to toggle the
            advisory due-list. Orange accent, highlighted while active. */}
        <Tooltip title="Patients whose latest completed assessment is past the reassessment interval. Click to view the list.">
          <Box
            textAlign="center"
            onClick={() => setRefillTabActive((v) => !v)}
            sx={{
              cursor: 'pointer',
              px: 1,
              borderRadius: 1,
              border: 1,
              borderColor: refillTabActive ? REFILL_ACCENT : 'transparent',
              bgcolor: refillTabActive ? 'rgba(237,108,2,0.10)' : 'transparent',
              transition: 'background-color 120ms',
              '&:hover': { bgcolor: 'rgba(237,108,2,0.14)' },
            }}
          >
            <Typography variant="h6" sx={{ color: REFILL_ACCENT }}>
              {refillLoading ? '…' : refillDueList.length}
            </Typography>
            <Typography variant="caption" color="textSecondary">Refill Assessment</Typography>
          </Box>
        </Tooltip>
      </Box>

      {/* GP-927: Refill Assessment advisory list — shown when its tile/tab is active. */}
      {refillTabActive && renderRefillAssessmentView()}

      {!refillTabActive && (
       <>
      {/* Wireframe Filters: Status | Due Date | Linked Status + Search */}
      <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 1, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} onChange={(e) => updateFilter('filterStatus', e.target.value)} label="Status">
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="completed">Complete</MenuItem>
            <MenuItem value="declined">Declined</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
            <MenuItem value="overdue">Overdue</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Due Date</InputLabel>
          <Select value={filterDueDate} onChange={(e) => updateFilter('filterDueDate', e.target.value)} label="Due Date">
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="today">Today</MenuItem>
            <MenuItem value="tomorrow">Tomorrow</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Linked Status</InputLabel>
          <Select value={filterLinkedStatus} onChange={(e) => updateFilter('filterLinkedStatus', e.target.value)} label="Linked Status">
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="linked">Linked</MenuItem>
            <MenuItem value="unlinked">Unlinked</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          type="date"
          label="Created From"
          InputLabelProps={{ shrink: true }}
          value={createdDateFrom}
          onChange={(e) => updateFilter('createdDateFrom', e.target.value)}
          sx={{ minWidth: 150 }}
          inputProps={{ max: createdDateTo || undefined }}
        />
        <TextField
          size="small"
          type="date"
          label="Created To"
          InputLabelProps={{ shrink: true }}
          value={createdDateTo}
          onChange={(e) => updateFilter('createdDateTo', e.target.value)}
          sx={{ minWidth: 150 }}
          inputProps={{ min: createdDateFrom || undefined }}
        />
        {(createdDateFrom || createdDateTo) && (
          <Button
            size="small"
            onClick={() => { updateFilter('createdDateFrom', ''); updateFilter('createdDateTo', ''); }}
            sx={{ textTransform: 'none' }}
          >
            Clear dates
          </Button>
        )}
        <TextField
          size="small" sx={{ flex: 1, minWidth: 150 }} placeholder="Search..."
          // Stays UNCONTROLLED (defaultValue + debounced commit) — a controlled
          // value or a key tied to searchTerm would remount the input mid-typing
          // and steal focus. GP-1337 only adds the mount-time restore of a
          // persisted term, plus the ref so Clear filters can blank it.
          inputRef={searchInputRef}
          defaultValue={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start">🔍</InputAdornment> }}
        />
        {/* GP-1337: filters now persist across visits, so there must be one press
            that gets back to the unfiltered queue. */}
        {hasActiveFilters && (
          <Button
            size="small"
            onClick={() => {
              // Drop a debounce still in flight, or it would re-commit the term
              // we are clearing 400ms later.
              if (searchTimeout.current) clearTimeout(searchTimeout.current);
              if (searchInputRef.current) searchInputRef.current.value = '';
              resetFilters();
            }}
            sx={{ textTransform: 'none' }}
          >
            Clear filters
          </Button>
        )}
      </Box>

      {/* GP-1337: bulk action bar — 132 Overdue tasks could only be routed one
          record at a time before this. */}
      {effectiveSelectedIds.length > 0 && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover' }}
        >
          <Typography variant="body2" fontWeight={600}>
            {effectiveSelectedIds.length} selected
          </Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={<PersonAddIcon sx={{ fontSize: '0.9rem' }} />}
            disabled={assigning}
            onClick={() => setAssignDialogOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            Assign to…
          </Button>
          <Button
            size="small"
            color="inherit"
            disabled={assigning}
            onClick={() => void assignTasks(effectiveSelectedIds, '').then(() => setSelectedIds([]))}
            sx={{ textTransform: 'none' }}
          >
            Unassign
          </Button>
          <Button size="small" color="inherit" onClick={() => setSelectedIds([])} sx={{ textTransform: 'none' }}>
            Clear selection
          </Button>
        </Stack>
      )}

      {/* Task Table — Wireframe Columns */}
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {loading && (
          <Box display="flex" justifyContent="center" p={3}><CircularProgress size={24} /></Box>
        )}

        {!loading && displayItems.length === 0 ? (
          <Box p={4} textAlign="center">
            <Typography variant="body2" color="textSecondary">No tasks found</Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0 }}>
            <Table
              size="small"
              stickyHeader
              sx={{
                '& .MuiTableCell-root': { py: 0.75, px: 1 },
                '& .MuiTableCell-head': { bgcolor: headerStripSurface, fontWeight: 600, fontSize: '0.72rem', whiteSpace: 'nowrap' },
              }}
            >
              <TableHead>
                <TableRow>
                  {/* GP-1337: row selection for bulk assign */}
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allSelectableSelected}
                      indeterminate={effectiveSelectedIds.length > 0 && !allSelectableSelected}
                      disabled={selectableIds.length === 0}
                      onChange={toggleSelectAll}
                      inputProps={{ 'aria-label': 'Select all tasks' }}
                    />
                  </TableCell>
                  <TableCell>Entry Method</TableCell>
                  <TableCell>Troubleshooting Reason</TableCell>
                  <TableCell sortDirection={sortBy === 'createdAt' ? (sortOrder === 'ASC' ? 'asc' : 'desc') : false}>
                    <TableSortLabel
                      active={sortBy === 'createdAt'}
                      direction={sortBy === 'createdAt' ? (sortOrder === 'ASC' ? 'asc' : 'desc') : 'desc'}
                      onClick={() => toggleSort('createdAt')}
                    >
                      Task Created Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Task Type</TableCell>
                  <TableCell sortDirection={sortBy === 'dueDate' ? (sortOrder === 'ASC' ? 'asc' : 'desc') : false}>
                    <TableSortLabel
                      active={sortBy === 'dueDate'}
                      direction={sortBy === 'dueDate' ? (sortOrder === 'ASC' ? 'asc' : 'desc') : 'desc'}
                      onClick={() => toggleSort('dueDate')}
                    >
                      Due Date
                    </TableSortLabel>
                  </TableCell>
                  {/* GP-1337: sits beside Due Date because it is the tie-breaker
                      when a whole page shares one due date — "this patient isn't
                      getting a delivery till next week, this one's getting one
                      tomorrow, the priority should be different". */}
                  <TableCell sortDirection={sortBy === 'deliveryDate' ? (sortOrder === 'ASC' ? 'asc' : 'desc') : false}>
                    <Tooltip title="When the patient actually receives their delivery — sort by this to triage tasks that share a due date" placement="top">
                      <TableSortLabel
                        active={sortBy === 'deliveryDate'}
                        direction={sortBy === 'deliveryDate' ? (sortOrder === 'ASC' ? 'asc' : 'desc') : 'desc'}
                        onClick={() => toggleSort('deliveryDate')}
                      >
                        Delivery Date
                      </TableSortLabel>
                    </Tooltip>
                  </TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Patient Demographics</TableCell>
                  <TableCell>Rx # / Drug</TableCell>
                  <TableCell>Order Linked</TableCell>
                  <TableCell>Assigned To (User)</TableCell>
                  <TableCell>Task Notes</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayItems.map((task) => {
                  const statusDisplay = getStatusDisplay(task);
                  const linkDisplay = getLinkStatusDisplay(task.linkStatus);
                  const latestNote = task.activityLog?.filter(l => l.note)?.slice(-1)[0];
                  const noteText = task.description || latestNote?.note || '';
                  const isUnlinked = task.linkStatus !== 'linked';

                  return (
                    <TableRow
                      key={task.id}
                      hover
                      onClick={() => setDetailView(task)}
                      sx={{
                        cursor: 'pointer',
                        opacity: task.status === 'completed' ? 0.65 : 1,
                        bgcolor: task.status === 'completed' ? tintSuccessCard
                          : isOverdue(task) ? stickyTintUrgent
                          : task.status === 'in_progress' ? tintWarning
                          : 'inherit',
                      }}
                    >
                      {/* Selection (GP-1337) — stopPropagation so ticking a row
                          doesn't also open its detail pane */}
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          size="small"
                          checked={effectiveSelectedIdSet.has(task.id)}
                          disabled={!isAssignable(task)}
                          onChange={() => toggleRowSelected(task.id)}
                          inputProps={{ 'aria-label': `Select task ${task.title}` }}
                        />
                      </TableCell>

                      {/* Entry Method */}
                      <TableCell>
                        <Typography variant="body2" fontSize="0.75rem">
                          {task.entryMethod || '—'}
                        </Typography>
                      </TableCell>

                      {/* Troubleshooting Reason */}
                      <TableCell sx={{ maxWidth: 180 }}>
                        <Tooltip title={task.troubleshootingReason || ''} placement="top">
                          <Typography variant="body2" fontSize="0.75rem" noWrap sx={{ maxWidth: 180 }}>
                            {task.troubleshootingReason || '—'}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      {/* Task Created Date */}
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" fontSize="0.75rem">
                          {formatCreatedDate(task.createdAt)}
                        </Typography>
                      </TableCell>

                      {/* Task Type */}
                      <TableCell>
                        <Typography variant="body2" fontSize="0.75rem" fontWeight={500}>
                          {getTaskTypeLabel(task.taskType)}
                        </Typography>
                      </TableCell>

                      {/* Due Date (read-only, greyed out) */}
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {task.dueDate ? (
                          <Typography
                            variant="body2"
                            fontSize="0.75rem"
                            color={isOverdue(task) ? 'error.main' : 'text.disabled'}
                            fontWeight={isOverdue(task) ? 600 : 400}
                          >
                            {format(parseISO(task.dueDate + 'T00:00:00'), 'M/d/yyyy')}
                            {isOverdue(task) && <WarningIcon sx={{ fontSize: 11, ml: 0.5, verticalAlign: 'middle' }} />}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>

                      {/* Delivery Date (GP-1337) */}
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {task.deliveryDate ? (
                          <Typography variant="body2" fontSize="0.75rem">
                            {formatTaskDate(task.deliveryDate)}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>

                      {/* Status (Red/Yellow/Green) */}
                      <TableCell>
                        <Chip
                          label={statusDisplay.label}
                          size="small"
                          color={statusDisplay.color}
                          sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                      </TableCell>

                      {/* Patient Demographics: Name, DOB, Kroll ID */}
                      <TableCell>
                        {task.patient?.name ? (
                          <Box>
                            {task.patient.id && activePharmacy?.id ? (
                              <Typography
                                variant="body2"
                                fontSize="0.75rem"
                                fontWeight={500}
                                sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline', '&:hover': { color: 'primary.dark' } }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const pid = task.patient!.id!;
                                  const compositeId = pid.includes('_') ? pid : `${activePharmacy.id}_${pid}`;
                                  navigate(`/patients/${compositeId}`);
                                }}
                              >
                                {task.patient.name}
                              </Typography>
                            ) : (
                              <Typography variant="body2" fontSize="0.75rem" fontWeight={500}>
                                {task.patient.name}
                              </Typography>
                            )}
                            {task.patientDob && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                DOB: {task.patientDob}
                              </Typography>
                            )}
                            {task.krollId && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                ID: {task.krollId}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>

                      {/* Rx # / Drug */}
                      <TableCell sx={{ maxWidth: 180 }}>
                        {task.rxNumber || extractDrugNameFromDescription(task.description) ? (
                          <Box>
                            {task.rxNumber && (
                              <Typography
                                variant="body2"
                                fontSize="0.75rem"
                                fontWeight={600}
                                sx={{ fontFamily: 'monospace' }}
                              >
                                {task.rxNumber}
                              </Typography>
                            )}
                            {(() => {
                              const drug = extractDrugNameFromDescription(task.description);
                              return drug ? (
                                <Tooltip title={drug} placement="top">
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    noWrap
                                    sx={{ maxWidth: 180, display: 'block' }}
                                  >
                                    {drug}
                                  </Typography>
                                </Tooltip>
                              ) : null;
                            })()}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>

                      {/* Order Linked */}
                      <TableCell>
                        <Chip
                          label={linkDisplay.label}
                          size="small"
                          color={linkDisplay.color}
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                      </TableCell>

                      {/* Assigned To (User) — GP-1337: an editable picker, not the
                          dead "Unassigned" text this used to render. Options are
                          restricted to the roles the TASK'S DEPARTMENT allows
                          (PRX-67 D7), which the write path re-checks.
                          stopPropagation keeps opening the dropdown from also
                          opening the detail pane. */}
                      <TableCell sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                        {isAssignable(task) ? (
                          <AssignedToCell
                            rowId={task.id}
                            assignedTo={task.assignedTo}
                            availableUsers={getAssignableUsers(task.department)}
                            userIdToNameMap={pharmacyUsersData?.userIdToNameMap}
                            onAssignmentChange={handleRowAssignmentChange}
                            includeAssignToMe={canAssignToSelfFor(task.department)}
                            disabled={assigning}
                            minWidth={200}
                            noOptionsText={noAssignableUsersText(task.department)}
                          />
                        ) : task.assignedTo ? (
                          <Typography variant="body2" fontSize="0.75rem">{resolveUserName(task.assignedTo, task.assignedToName)}</Typography>
                        ) : (
                          <Typography variant="caption" color="text.disabled">Unassigned</Typography>
                        )}
                      </TableCell>

                      {/* Task Notes */}
                      <TableCell sx={{ maxWidth: 180 }}>
                        {noteText ? (
                          <Tooltip title={noteText} placement="top">
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 180, display: 'block' }}>
                              {noteText}
                            </Typography>
                          </Tooltip>
                        ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>

                      {/* Actions — Start Counseling for counseling tasks, Complete/Acknowledge for others (R10) */}
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {isCounselingSuperseded(task) ? (
                          // GP-1203: no action — counseling already happened on the
                          // rebuilt Rx. The chip names it so staff don't have to
                          // cross-reference Fill History and Counseling History.
                          <Tooltip title={supersededByLabel(task)} placement="top">
                            <Chip
                              label={task.supersededByRxNumber ? `Superseded by ${task.supersededByRxNumber}` : 'Superseded'}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                          </Tooltip>
                        ) : task.status !== 'completed' ? (
                          task.type === 'counseling' ? (
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              disabled={counselingLoading}
                              startIcon={<CounselIcon sx={{ fontSize: '0.85rem' }} />}
                              onClick={(e) => handleStartCounselingFromTable(task, e)}
                              sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0.25, px: 1, whiteSpace: 'nowrap' }}
                            >
                              Start Counseling
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              disabled={isUnlinked}
                              onClick={(e) => handleCompleteTask(task, e)}
                              sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0.25, px: 1, whiteSpace: 'nowrap' }}
                            >
                              {getTaskCloseLabel(task)}
                            </Button>
                          )
                        ) : (
                          <Chip label="Done" size="small" color="success" sx={{ height: 20, fontSize: '0.65rem' }} />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
      {/* Pagination — pinned below the scroll container so it's always visible */}
      {response?.pagination && (
        <TablePagination
          component="div"
          // 'overdue'/'declined' are client-side filters (no backend status param),
          // so the backend total would overcount — use the filtered row count.
          count={(filterStatus === 'overdue' || filterStatus === 'declined') ? displayItems.length : (response?.pagination?.total || 0)}
          page={Math.max(0, (response.pagination.page || 1) - 1)}
          onPageChange={(_, newPage) => { setPage(newPage + 1); setSelectedIds([]); }}
          rowsPerPage={limit}
          rowsPerPageOptions={[50, 100, 250, 500]}
          onRowsPerPageChange={(e) => {
            setLimit(parseInt(e.target.value, 10));
            setPage(1);
            setSelectedIds([]);
          }}
          sx={{ borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0 }}
        />
      )}
       </>
      )}
    </Box>
  );

  return (
    <>
      {detailView ? renderDetailView() : renderListView()}

      {/* Create/Edit Task Dialog */}
      <CreateTaskDialog
        open={taskDialog}
        onClose={() => setTaskDialog(false)}
        editingTask={editingTask}
        onSave={handleSaveTask}
        saving={saving}
      />

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>

      {/* Bulk Assign Dialog (GP-1337) — mirrors the Billing Queue's assign flow */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => { setAssignDialogOpen(false); setAssignTargetUser(''); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Assign Tasks to User</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Assign {effectiveSelectedIds.length} task(s) to:
            </Typography>
            {bulkAssignableUsers.length === 0 ? (
              <Alert severity="warning">{noAssignableUsersText(bulkSelectedDepartments)}.</Alert>
            ) : (
              <FormControl fullWidth>
                <InputLabel>Select User</InputLabel>
                <Select
                  value={assignTargetUser}
                  label="Select User"
                  onChange={(e) => setAssignTargetUser(e.target.value)}
                >
                  {bulkAssignableUsers.map((u) => (
                    <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAssignDialogOpen(false); setAssignTargetUser(''); }}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleBulkAssign}
            disabled={!assignTargetUser || assigning}
          >
            {assigning ? <CircularProgress size={18} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Outreach attempt / snooze dialog (GP-1365) — records WHAT was tried and
          moves the due date in ONE call, so the note and the queue's overdue
          bucket can never disagree the way a free-text note did. */}
      <Dialog
        open={outreachOpen && !!detailView}
        onClose={() => setOutreachOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Log Outreach Attempt</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!outreachHasLegalDate && (
              <Alert severity="warning">
                The delivery is today ({formatSnoozeDate(todayString)}), so this follow-up
                cannot be deferred — any later date would fall after the medication arrives,
                and today would leave the task overdue. Complete or decline the counseling
                instead.
              </Alert>
            )}

            {!!detailView?.outreachAttempts && detailView.outreachAttempts >= OUTREACH_ESCALATION_THRESHOLD && (
              <Alert severity="warning">
                {detailView.outreachAttempts} attempts have already been logged on this task.
                Consider escalating instead of deferring it again.
              </Alert>
            )}

            <FormControl fullWidth size="small">
              <InputLabel id="outreach-outcome-label">Outcome</InputLabel>
              <Select
                labelId="outreach-outcome-label"
                value={outreachOutcome}
                label="Outcome"
                onChange={(e) => setOutreachOutcome(e.target.value as OutreachOutcomeValue)}
              >
                {OUTREACH_OUTCOMES.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                New due date
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                {OUTREACH_SNOOZE_PRESET_DAYS.map((days) => {
                  const value = snoozePresetDate(todayString, days);
                  const blocked = !!outreachMaxDate && value > outreachMaxDate;
                  return (
                    <Button
                      key={days}
                      size="small"
                      variant={outreachDueDate === value ? 'contained' : 'outlined'}
                      disabled={blocked}
                      onClick={() => setOutreachDueDate(value)}
                      sx={{ textTransform: 'none', borderRadius: 1.5 }}
                    >
                      +{days} days
                    </Button>
                  );
                })}
              </Stack>
              <TextField
                type="date"
                size="small"
                fullWidth
                value={outreachDueDate}
                onChange={(e) => setOutreachDueDate(e.target.value)}
                disabled={!outreachHasLegalDate}
                inputProps={{ min: outreachMinDate, max: outreachMaxDate }}
                error={!!outreachDueDateError}
                helperText={
                  outreachDueDateError ||
                  (outreachMaxDate
                    ? `Delivery is ${formatSnoozeDate(outreachMaxDate)} — the follow-up must happen before it.`
                    : `Earliest is ${formatSnoozeDate(outreachMinDate)}; a task due today stays in the Overdue bucket.`)
                }
              />
            </Box>

            <TextField
              label="Note"
              size="small"
              fullWidth
              multiline
              minRows={2}
              placeholder="e.g. Left voicemail on cell, asked to call back"
              value={outreachNote}
              onChange={(e) => setOutreachNote(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOutreachOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleLogOutreach}
            disabled={
              !outreachOutcome || !outreachDueDate || !!outreachDueDateError
              || !outreachHasLegalDate || loggingOutreach
            }
          >
            {loggingOutreach ? <CircularProgress size={18} /> : 'Save & Reschedule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for completing tasks */}
      <Dialog open={confirmCompleteOpen} onClose={() => { setConfirmCompleteOpen(false); setTaskToComplete(null); }}>
        <DialogTitle>Confirm Task Completion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to mark this task as complete?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmCompleteOpen(false); setTaskToComplete(null); }}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleConfirmComplete}>
            Confirm Complete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Counseling Dialog */}
      {detailView && counselingDialogOpen && counselingOrderData && (
        <CounselingDialog
          // GP-1081: key on the opened order/drug so switching orders remounts
          // the dialog fresh — every drug-bound region (drug card, checklist, FDB
          // reference) is rebuilt for the new order instead of carrying stale
          // state from the previously counseled order.
          key={`${counselingOrderData.orderId ?? 'none'}:${counselingOrderData.rxNumber ?? ''}:${counselingOrderData.din ?? ''}:${counselingExistingRecord?.id ?? 'new'}`}
          open={counselingDialogOpen}
          onClose={() => { setCounselingDialogOpen(false); setCounselingOrderData(null); setCounselingExistingRecord(null); setDetailView(null); fetchTasks(); onClose?.(); }}
          orderData={counselingOrderData as any}
          existingRecordId={counselingExistingRecord?.id}
          existingChecklist={counselingExistingRecord?.checklist}
          existingStatus={counselingExistingRecord?.status}
          existingCounselingBy={counselingExistingRecord?.counselingBy}
          existingMethod={counselingExistingRecord?.method}
          existingComments={counselingExistingRecord?.comments}
          existingDeclinedReason={counselingExistingRecord?.declinedReason}
          existingAgentName={counselingExistingRecord?.agentName}
          existingAgentRelationship={counselingExistingRecord?.agentRelationship}
          existingDateCounselled={counselingExistingRecord?.dateCounselled}
          counselingOrigin="task_queue"
          onComplete={async () => {
            setCounselingDialogOpen(false);
            setCounselingOrderData(null);
            setCounselingExistingRecord(null);
            // Mark the counseling task as complete after counseling is finalized
            let taskCompleted = false;
            if (detailView && detailView.type === 'counseling' && detailView.status !== 'completed') {
              try {
                await updateManualTask(detailView.id, { status: 'completed' });
                taskCompleted = true;
              } catch { /* task completion is best-effort after counseling finishes */ }
            }
            setDetailView(null);
            fetchTasks();
            // PRX-67 (wave 1 review item 5, Medium) — completing the task here
            // moves it out of the open-count tallies; without this the
            // department chip and the sidebar badge stayed stale.
            invalidateCounts();
            setSnackbar({
              open: true,
              message: taskCompleted
                ? 'Counseling completed and task finalized'
                : 'Counseling completed, but task could not be marked complete',
              severity: taskCompleted ? 'success' : 'warning',
            });
            onClose?.();
          }}
        />
      )}
    </>
  );
};
